import { useEffect, useRef, useState } from "react";

// Lot 619 — story photo (ou texte) + musique : durée calée sur la musique, plafonnée par le réglage admin
// settings.story_music_image_seconds (défaut 15 s). La barre suit la position RÉELLE de la musique ; si la
// musique ne démarre pas (bloquée, réseau), un minuteur prend le relais après un court délai de grâce.

export const MUSIC_STORY_DEFAULT_SEC = 15;
const GRACE_SEC = 2.5;

export const musicStorySeconds = (config) => {
  const n = Number(config?.settings?.story_music_image_seconds);
  return n >= 3 ? n : MUSIC_STORY_DEFAULT_SEC;
};

export const isMusicTimedStory = (story) => !!story && story.media_type !== "video" && !!story.music_url;

// Pur (testable) : {elapsed, total} à partir de l'état de l'<audio> et de l'horloge murale (secondes).
export const musicProgress = ({ cur = null, musicDur = null, startAt = 0, limit, wall, maxSeen = 0 }) => {
  let total = limit;
  if (Number.isFinite(musicDur) && musicDur > startAt + 0.5) total = Math.min(limit, musicDur - startAt);
  let elapsed = Math.max(0, wall - GRACE_SEC); // plancher : la story n'est jamais bloquée
  let seen = maxSeen;
  if (cur != null && Number.isFinite(cur)) {
    const c = Math.max(0, cur - startAt);
    if (c + 1 < maxSeen) elapsed = total; // musique rebouclée (plus courte que la limite) → passe terminée
    else { seen = Math.max(maxSeen, c); elapsed = Math.max(elapsed, c); }
  }
  return { elapsed: Math.min(elapsed, total), total, maxSeen: seen, done: elapsed >= total - 0.05 };
};

export const useMusicStoryProgress = ({ active, audioRef, storyKey, startAt = 0, limitSec, paused = false, onDone }) => {
  const [pct, setPct] = useState(0);
  const doneRef = useRef(false);
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;
  useEffect(() => { setPct(0); doneRef.current = false; }, [storyKey]);
  useEffect(() => {
    if (!active || paused) return undefined;
    let raf = 0, startTs = null, last = -1, maxSeen = 0;
    const loop = (ts) => {
      if (startTs == null) startTs = ts;
      const a = audioRef.current;
      const ready = a && !a.error && a.readyState >= 2;
      const r = musicProgress({
        cur: ready ? a.currentTime : null,
        musicDur: ready ? a.duration : null,
        startAt, limit: limitSec, wall: (ts - startTs) / 1000, maxSeen,
      });
      maxSeen = r.maxSeen;
      const p = Math.min(100, (r.elapsed / r.total) * 100);
      if (Math.abs(p - last) >= 0.4) { last = p; setPct(p); }
      if (r.done) { if (!doneRef.current) { doneRef.current = true; onDoneRef.current?.(); } return; }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [active, paused, storyKey, startAt, limitSec, audioRef]);
  return pct;
};
